import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Book as BookIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { LuNotebookPen } from 'react-icons/lu';
import { GoBook } from 'react-icons/go';
import { useDispatch } from 'react-redux';
import ConfirmationDialog from '../UIElements/ConfirmationDialog';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import {
  createExamination,
  deleteExamination,
  getExaminationsList,
  updateExamination,
} from '../../features/examinations/examinationSlice';
import useNotification from '../../hooks/useNotification';
import ToastNotification from '../UIElements/ToastNotification';

const ExaminationTypes = () => {
  const dispatch = useDispatch();
  const {
    dialog,
    showDeleteConfirmation,
    closeDialog: closeConfirmDialog,
  } = useConfirmationDialog();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();
  const [examinationTypes, setExaminationTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newExaminationType, setNewExaminationType] = useState({
    examType: '',
  });

  const [editingExaminationType, setEditingExaminationType] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'

  // Fetch ExaminationTypes on component mount
  useEffect(() => {
    fetchExaminationTypes();
  }, []);

  const fetchExaminationTypes = async () => {
    try {
      setLoading(true);
      const data = await dispatch(getExaminationsList()).unwrap();
      setExaminationTypes(data);
    } catch (error) {
      showError('Failed to fetch Examination Types. Please try again.');
      console.error('Error fetching Examination Types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExaminationType = async () => {
    if (!newExaminationType.examType.trim()) {
      showError('Please enter a ExaminationType name');
      return;
    }

    try {
      setLoading(true);
      const res = await dispatch(
        createExamination({
          examType: newExaminationType.examType.trim(),
        }),
      ).unwrap();

      console.log('Create ExaminationType response:', res);

      // Refresh the ExaminationTypes list
      await fetchExaminationTypes();

      setNewExaminationType({ examType: '' });
      setOpenDialog(false);
      showSuccess('Examination Type added successfully!');
    } catch (error) {
      showError(error || 'Failed to add ExaminationType. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditExaminationType = (ExaminationType) => {
    setEditingExaminationType(ExaminationType);
    setNewExaminationType({
      examType: ExaminationType.examType || '', // Handle different property names
    });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  const handleUpdateExaminationType = async () => {
    if (!newExaminationType.examType.trim()) {
      showError('Please enter a ExaminationType name');
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        updateExamination({
          id: editingExaminationType.id,
          examType: newExaminationType.examType.trim(),
        }),
      ).unwrap();

      // Refresh the ExaminationTypes list
      await fetchExaminationTypes();

      setNewExaminationType({ examType: '' });
      setEditingExaminationType(null);
      setOpenDialog(false);
      showSuccess('ExaminationType updated successfully!');
    } catch (error) {
      showError(error || 'Failed to update ExaminationType. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExaminationType = async (id) => {
    // Find the ExaminationType name for display in confirmation
    const ExaminationType = examinationTypes.find((s) => s.id === id);
    const examType = ExaminationType?.examType || 'this ExaminationType';

    showDeleteConfirmation(examType, () => confirmDeleteExaminationType(id));
  };

  const confirmDeleteExaminationType = async (id) => {
    try {
      setLoading(true);
      await dispatch(deleteExamination(id)).unwrap();

      // Refresh the ExaminationTypes list
      await fetchExaminationTypes();
      showSuccess('ExaminationType deleted successfully!');
      closeConfirmDialog();
    } catch (error) {
      if (error == 'examType mapped') {
        showError("ExamType is scheduled, so it can't be deleted.");
      } else {
        showError('Failed to delete ExaminationType. Please try again.');
      }
      console.error('Error deleting ExaminationType:', error);
      closeConfirmDialog();
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setNewExaminationType({ examType: '' });
    setEditingExaminationType(null);
    setDialogMode('add');
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setNewExaminationType({ examType: '' });
    setEditingExaminationType(null);
  };

  const totalExaminationTypes = examinationTypes.length;

  return (
    <div className='space-y-6'>
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Header with Statistics */}
      <Card>
        <CardContent>
          <Box className='flex items-center justify-between mb-4 flex-wrap gap-2'>
            <Box className='flex items-center gap-2 flex-wrap'>
              <Box className='size-8 bg-blue-500 rounded-lg flex items-center justify-center'>
                <GoBook className='text-white size-5' />
              </Box>
              <Typography variant='h6' className='text-blue-700 font-semibold'>
                ExaminationType Management
              </Typography>
            </Box>
            <Button
              variant='contained'
              color='primary'
              startIcon={<AddIcon />}
              onClick={openAddDialog}
              size='small'
              disabled={loading}
            >
              Add New Examination Type
            </Button>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{ backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}
              >
                <CardContent className='text-center py-3'>
                  <Typography variant='h4' className='font-bold text-blue-600'>
                    {totalExaminationTypes}
                  </Typography>
                  <Typography variant='body2' className='text-blue-700'>
                    Total Examination Types
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Examination Types Table */}
      <Card>
        <CardContent>
          <Typography variant='h6' className='mb-4 text-blue-700 font-semibold'>
            Existing Examination Types
          </Typography>

          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ maxHeight: '60vh', overflow: 'auto' }}
          >
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>
                    <strong>S.No</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Examination Type Name</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {examinationTypes.map((examinationType, index) => (
                  <TableRow
                    key={examinationType.id}
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box className='flex items-center gap-2'>
                        <LuNotebookPen
                          // fontSize='small'
                          className='text-blue-500 size-4'
                        />
                        <Typography variant='body2' className='font-medium'>
                          {examinationType.examType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Box className='flex justify-center gap-1'>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() =>
                            handleEditExaminationType(examinationType)
                          }
                          disabled={loading}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() =>
                            handleDeleteExaminationType(examinationType.id)
                          }
                          disabled={loading}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {examinationTypes.length === 0 && !loading && (
            <Box className='text-center py-8'>
              <BookIcon
                className='text-gray-300 mb-2'
                style={{ fontSize: 48 }}
              />
              <Typography variant='h6' className='text-gray-400 mb-2'>
                No Examination Types Found
              </Typography>
              <Typography variant='body2' className='text-gray-500'>
                Click "Add New Examination Type" to create your first
                examination type
              </Typography>
            </Box>
          )}

          {loading && examinationTypes.length === 0 && (
            <Box className='text-center py-8'>
              <CircularProgress />
              <Typography variant='body2' className='text-gray-500 mt-2'>
                Loading examination types...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit ExaminationType Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          {dialogMode === 'add'
            ? 'Add New Examination Type'
            : 'Edit Examination Type'}
        </DialogTitle>
        <DialogContent>
          <Box className='space-y-4 pt-2'>
            <TextField
              fullWidth
              label='Examination Type'
              value={newExaminationType.examType}
              onChange={(e) =>
                setNewExaminationType({
                  ...newExaminationType,
                  examType: e.target.value,
                })
              }
              placeholder='Enter examination type name'
              required
              disabled={loading}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions className='flex flex-wrap gap-y-2'>
          <Button
            variant='outlined'
            onClick={closeDialog}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={
              dialogMode === 'add'
                ? handleAddExaminationType
                : handleUpdateExaminationType
            }
            variant='contained'
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={loading || !newExaminationType.examType.trim()}
          >
            {loading
              ? 'Processing...'
              : dialogMode === 'add'
                ? 'Add ExaminationType'
                : 'Update ExaminationType'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={dialog.open}
        onClose={closeConfirmDialog}
        onConfirm={dialog.onConfirm}
        title={dialog.title}
        message={dialog.message}
        severity={dialog.severity}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        loading={loading}
      />
      {/* Toast Notification */}
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </div>
  );
};

export default ExaminationTypes;
